import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type IButtonGroupProps,
    DropdownMenu,
    ButtonGroup,
    Button,
    Toggle
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Caption,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    HeartIcon,
    HomeIcon,
    UserIcon,
    FileIcon
} from "../../anatomy/icons";

const NAME = "ButtonGroup";

const USAGE_CODE = `const [format, setFormat] = useState("PDF");

const [isExporting, setIsExporting] = useState(false);

<ButtonGroup
    title="Export report"
    variant="filled"
    type="primary"
>
    <Button
        onPress={() => setIsExporting(true)}
        title={\`Export as \${format}\`}
        isLoading={isExporting}
        icon={FileIcon}
    />
    <ButtonGroup.Separator />
    <ButtonGroup.Menu
        title="More formats"
    >
        <DropdownMenu.Content>
            <DropdownMenu.Item
                onPress={() => setFormat("PDF")}
                title="PDF"
            />
            <DropdownMenu.Item
                onPress={() => setFormat("Excel")}
                title="Excel"
            />
        </DropdownMenu.Content>
    </ButtonGroup.Menu>
</ButtonGroup>`;

const COMPOSITION_CODE = `const [isLiked, setIsLiked] = useState(false);

const likes = 128 + (isLiked ? 1 : 0);

<ButtonGroup
    title="Likes"
>
    <Toggle
        onPressedChange={setIsLiked}
        isPressed={isLiked}
        variant="outline"
        icon={HeartIcon}
        title="Like"
    />
    <ButtonGroup.Text
        title={String(likes)}
    />
</ButtonGroup>`;

const VARIANTS = [
    "outline",
    "tonal",
    "filled"
] as const;

const TYPES = [
    "neutral",
    "primary",
    "danger",
    "success"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const FORMATS = [
    "PDF",
    "Excel",
    "CSV"
] as const;

const EXPORT_DELAY = 1200;

const LIKES = 128;

const DEMO_ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
};

const DEMO_CONTENT_STYLE: CSSProperties = {
    justifyContent: "center",
    display: "flex",
    width: "100%"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("buttonGroupPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("buttonGroupPage.subcomponentsTitle"),
        id: "subcomponents"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DemoItem = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={DEMO_ITEM_STYLE}
>
    <div
        style={DEMO_CONTENT_STYLE}
    >
        {children}
    </div>
    <Caption>
        {label}
    </Caption>
</div>;

const ButtonGroupPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        format,
        setFormat
    ] = useState<string>(FORMATS[0]);

    const [
        isExporting,
        setIsExporting
    ] = useState(false);

    const [
        isLiked,
        setIsLiked
    ] = useState(false);

    useEffect(() => {
        if(!isExporting) {
            return;
        }

        const timer = window.setTimeout(() => setIsExporting(false), EXPORT_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isExporting
    ]);

    const likes = LIKES + (isLiked ? 1 : 0);

    const renderActions = (props: Pick<IButtonGroupProps, "spreadBehaviour" | "isDisabled" | "variant" | "type" | "size">, isLastDisabled = false) => <ButtonGroup
        title={t("buttonGroupPage.actionsTitle")}
        {...props}
    >
        <Button
            title={t("buttonGroupPage.copy")}
            onPress={() => undefined}
        />
        <ButtonGroup.Separator/>
        <Button
            title={t("buttonGroupPage.move")}
            onPress={() => undefined}
        />
        <ButtonGroup.Separator/>
        <Button
            title={t("buttonGroupPage.archive")}
            isDisabled={isLastDisabled}
            onPress={() => undefined}
        />
    </ButtonGroup>;

    return <Article>
        <ComponentHeader
            lead={t("buttonGroupPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("buttonGroupPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("buttonGroupPage.overviewCaption")}
            >
                <ButtonGroup
                    title={t("buttonGroupPage.exportTitle")}
                    variant="filled"
                    type="primary"
                >
                    <Button
                        title={t("buttonGroupPage.exportAs", [
                            format
                        ])}
                        onPress={() => setIsExporting(true)}
                        isLoading={isExporting}
                        icon={FileIcon}
                    />
                    <ButtonGroup.Separator/>
                    <ButtonGroup.Menu
                        title={t("buttonGroupPage.moreFormats")}
                    >
                        <DropdownMenu.Content>
                            {
                                FORMATS.map((item) => <DropdownMenu.Item
                                    onPress={() => setFormat(item)}
                                    title={item}
                                    key={item}
                                />)
                            }
                        </DropdownMenu.Content>
                    </ButtonGroup.Menu>
                </ButtonGroup>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("buttonGroupPage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((variant) => <DemoItem
                        label={variant}
                        key={variant}
                    >
                        {
                            renderActions({
                                variant
                            })
                        }
                    </DemoItem>)
                }
            </Preview>
            <Paragraph>
                {t("buttonGroupPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <DemoItem
                        label={type}
                        key={type}
                    >
                        {
                            renderActions({
                                variant: "filled",
                                type
                            })
                        }
                    </DemoItem>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("buttonGroupPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <DemoItem
                        label={size}
                        key={size}
                    >
                        {
                            renderActions({
                                variant: "tonal",
                                size
                            })
                        }
                    </DemoItem>)
                }
            </Preview>
        </Section>
        <Section
            title={t("buttonGroupPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("buttonGroupPage.orientationText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <DemoItem
                    label="vertical"
                >
                    <ButtonGroup
                        title={t("buttonGroupPage.quickTitle")}
                        orientation="vertical"
                    >
                        <Button
                            accessibilityLabel={t("sample.home")}
                            onPress={() => undefined}
                            icon={HomeIcon}
                        />
                        <Button
                            accessibilityLabel={t("sample.search")}
                            onPress={() => undefined}
                            icon={SearchIcon}
                        />
                        <Button
                            accessibilityLabel={t("sample.item1")}
                            onPress={() => undefined}
                            icon={UserIcon}
                        />
                    </ButtonGroup>
                </DemoItem>
                <DemoItem
                    label="stretch"
                >
                    {
                        renderActions({
                            spreadBehaviour: "stretch",
                            variant: "tonal"
                        })
                    }
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("buttonGroupPage.subcomponentsTitle")}
            id="subcomponents"
        >
            <Paragraph>
                {t("buttonGroupPage.subcomponentsText")}
            </Paragraph>
            <Preview>
                <ButtonGroup
                    title={t("buttonGroupPage.likesTitle")}
                >
                    <Toggle
                        title={t("buttonGroupPage.like")}
                        onPressedChange={setIsLiked}
                        isPressed={isLiked}
                        variant="outline"
                        icon={HeartIcon}
                    />
                    <ButtonGroup.Text
                        title={String(likes)}
                    />
                </ButtonGroup>
            </Preview>
            <CodeBlock
                code={COMPOSITION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("buttonGroupPage.statesText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label={t("buttonGroupPage.disabledGroup")}
                >
                    {
                        renderActions({
                            isDisabled: true
                        })
                    }
                </DemoItem>
                <DemoItem
                    label={t("buttonGroupPage.disabledItem")}
                >
                    {
                        renderActions({}, true)
                    }
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("buttonGroupPage.a11yRole"),
                    t("buttonGroupPage.a11yTabStops"),
                    t("buttonGroupPage.a11yFocus"),
                    t("buttonGroupPage.a11yMenu"),
                    t("buttonGroupPage.a11yIconOnly"),
                    t("buttonGroupPage.a11ySeparator"),
                    t("buttonGroupPage.a11yDisabled")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("buttonGroupPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("buttonGroupPage.propVariant"),
                        type: "\"outline\" | \"tonal\" | \"filled\"",
                        defaultValue: "\"outline\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("buttonGroupPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("buttonGroupPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("buttonGroupPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("buttonGroupPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("buttonGroupPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("buttonGroupPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("buttonGroupPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("buttonGroupPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("buttonGroupPage.menuPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("buttonGroupPage.propMenuTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "\"top\" | \"bottom\" | \"left\" | \"right\"",
                        description: t("buttonGroupPage.propMenuSide"),
                        defaultValue: "\"bottom\"",
                        name: "side"
                    },
                    {
                        description: t("buttonGroupPage.propMenuAlign"),
                        type: "\"start\" | \"center\" | \"end\"",
                        defaultValue: "\"end\"",
                        name: "align"
                    },
                    {
                        description: t("buttonGroupPage.propMenuIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("buttonGroupPage.propMenuInitialIsOpen"),
                        defaultValue: "false",
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("buttonGroupPage.propMenuOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        description: t("buttonGroupPage.propMenuIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("buttonGroupPage.propMenuCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("buttonGroupPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("buttonGroupPage.propMenuTestID"),
                        name: "testID",
                        type: "string"
                    },
                    {
                        description: t("buttonGroupPage.propMenuStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    },
                    {
                        description: t("buttonGroupPage.propMenuChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph>
                {t("buttonGroupPage.textPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("buttonGroupPage.propTextTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("buttonGroupPage.propTextIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("buttonGroupPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("buttonGroupPage.separatorNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ButtonGroupPage;
